import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaDiscord, FaList, FaGamepad, FaTrophy } from "react-icons/fa";
import Footer from "./Footer";
import { useAuth } from "../context/AuthContext";
import "../styles/GoogleButton.css"; // Import Google Button styles

// Removed UserData interface (now in context)
// Removed SERVER_URL, cookie utils (still needed here for auth check)
const SERVER_URL = process.env.REACT_APP_SERVER_HOST;
const isLocalhost = window.location.hostname === "localhost";

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() || null;
  return null;
};

const deleteCookie = (name: string) => {
  const cookieOptions = isLocalhost
    ? "path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT"
    : "domain=.vorlas.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `${name}=; ${cookieOptions}`;
};

const setCookie = (name: string, value: string) => {
  const cookieOptions = isLocalhost
    ? "path=/; samesite=lax"
    : "domain=.vorlas.com; path=/; secure; samesite=strict";
  document.cookie = `${name}=${value}; ${cookieOptions}`;
};

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  // Consume context instead of managing state here
  const { user, setUser, setIsLoading } = useAuth();
  const navigate = useNavigate();

  // Effect to check auth status on load and set context state
  useEffect(() => {
    setIsLoading(true); // Indicate loading
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get("token");
    const nicknameFromUrl = urlParams.get("nickname");

    if (tokenFromUrl) {
      setCookie("auth_token", tokenFromUrl);
      if (nicknameFromUrl) {
        setCookie("nickname", nicknameFromUrl);
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const checkAuth = async () => {
      const token = getCookie("auth_token");
      if (!token) {
        setUser(null);
        setIsLoading(false); // Finished loading
        return;
      }
      try {
        const response = await fetch(`${SERVER_URL}/auth/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          deleteCookie("auth_token");
          deleteCookie("nickname");
          setUser(null);
          throw new Error("Failed to fetch user data");
        }
        const userData = await response.json();
        setUser(userData);
      } catch (error) {
        console.error("Error during auth check:", error);
        deleteCookie("auth_token");
        deleteCookie("nickname");
        setUser(null);
      } finally {
        setIsLoading(false); // Finished loading (success or error)
      }
    };
    checkAuth();
    // Removed navigate dependency, context changes will trigger re-renders
  }, [setUser, setIsLoading]); // Depend on context setters

  // Login/Logout Handlers (still needed here to trigger actions)
  const handleLogin = () => {
    window.location.href = `${SERVER_URL}/auth/google`;
  };
  const handleDiscordLogin = () => {
    window.location.href = `${SERVER_URL}/auth/discord`;
  };
  const handleLogout = () => {
    deleteCookie("auth_token");
    deleteCookie("nickname");
    setUser(null); // Update context state
    if (window.location.pathname !== "/") {
      navigate("/");
    }
  };

  // Render layout using the user state from context
  return (
    <div className="flex flex-col min-h-screen">
      {/* Sticky Header Wrapper */}
      <div className="sticky top-0 z-50 bg-white shadow-md">
        {/* Top Bar with Centered Logo */}
        <div className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center items-center py-2">
              <img
                src="/logo.webp"
                alt="Vorlas Logo"
                className="h-16 w-auto my-2"
              />
            </div>
          </div>
        </div>

        {/* Second Navigation Bar */}
        <nav className="bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              {/* Navigation Links - Styled as blue buttons with icons */}
              <div className="flex items-center space-x-2">
                <Link
                  to="/"
                  className="flex items-center bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  <FaList className="w-4 h-4 mr-1.5" />
                  Lobbies
                </Link>
                <Link
                  to="/games"
                  className="flex items-center bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  <FaGamepad className="w-4 h-4 mr-1.5" />
                  Games
                </Link>
                <Link
                  to="/leaderboards"
                  className="flex items-center bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  <FaTrophy className="w-4 h-4 mr-1.5" />
                  Leaderboards
                </Link>
              </div>
              {/* Login/User Info Area */}
              <div className="flex items-center">
                {user ? (
                  <>
                    {user.avatar && (
                      <img
                        src={user.avatar}
                        alt="Profile"
                        className="h-8 w-8 rounded-full mr-2"
                        onError={(e) => {
                          console.warn(`Failed to load avatar: ${user.avatar}`);
                        }}
                      />
                    )}
                    <span className="text-gray-700 mr-4">{user.nick}</span>
                    <Link
                      to="/profile"
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium mr-2 transition-colors"
                    >
                      Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md text-sm"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      className="gsi-material-button"
                      onClick={handleLogin}
                    >
                      {/* ... google button content ... */}
                      <div className="gsi-material-button-state"></div>
                      <div className="gsi-material-button-content-wrapper">
                        <div className="gsi-material-button-icon">
                          <svg
                            version="1.1"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 48 48"
                            xmlnsXlink="http://www.w3.org/1999/xlink"
                            style={{ display: "block" }}
                          >
                            <path
                              fill="#EA4335"
                              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                            ></path>
                            <path
                              fill="#4285F4"
                              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                            ></path>
                            <path
                              fill="#FBBC05"
                              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                            ></path>
                            <path
                              fill="#34A853"
                              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                            ></path>
                            <path fill="none" d="M0 0h48v48H0z"></path>
                          </svg>
                        </div>
                        <span className="gsi-material-button-contents">
                          Sign in with Google
                        </span>
                        <span style={{ display: "none" }}>
                          Sign in with Google
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={handleDiscordLogin}
                      className="flex items-center justify-center bg-[#5865F2] hover:bg-[#4752C4] text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                      title="Sign in with Discord"
                    >
                      <FaDiscord className="w-5 h-5 mr-2" />
                      <span>Sign in with Discord</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content Area - takes remaining space and scrolls */}
      <main className="flex-grow max-w-7xl mx-auto px-6 lg:px-8 w-full overflow-y-auto py-6">
        {/* We need to pass the user state down to Lobbies component somehow
             Option 1: Pass user as prop via App.tsx (complex)
             Option 2: Use Context API 
             Option 3: Make Lobbies fetch its own needed state (isLoggedIn) 
             Let's modify Lobbies later if needed. For now, render children directly.
          */}
        {children}
      </main>

      {/* Sticky Footer */}
      <Footer className="sticky bottom-0 z-50" />
    </div>
  );
};

export default MainLayout;
